import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  List,
  Card,
  Row,
  Col,
  Input,
  Select,
  Button,
  Tag,
  Space,
  Spin,
  message,
  Empty,
  Grid,
  Popconfirm,
} from "antd";
import {
  ReloadOutlined,
  PhoneOutlined,
  FilterOutlined,
  SearchOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const getStoredToken = () => {
  const keys = ["token", "accessToken", "AuthToken", "jwtToken"];
  for (const k of keys) {
    const v = localStorage.getItem(k);
    if (v) return v.replace(/^"|"$/g, "");
  }
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.token || user?.accessToken || null;
  } catch {
    return null;
  }
};

const api = axios.create({
  baseURL: global.config.APIURL.BaseURL,
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`,
    };
  }
  if (config.withCredentials === undefined) {
    config.withCredentials = false;
  }
  return config;
});

const LeadsToApprove = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [approvingId, setApprovingId] = useState(null);

  const [search, setSearch] = useState("");
  const [inquiryFilter, setInquiryFilter] = useState(undefined);
  const [stateFilter, setStateFilter] = useState(undefined);
  const [visibleCount, setVisibleCount] = useState(10);

  useEffect(() => {
    fetchLeads(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeads = async (initial = false) => {
    try {
      if (initial) setLoading(true);
      else setFetching(true);

      const res = await api.get("api/HighIntentLeads/GetHighIntentLeads");
      const data = Array.isArray(res.data) ? res.data : [];
      setLeads(data);
      setVisibleCount(isMobile ? 10 : 20);
    } catch (err) {
      if (err?.response) {
        if (err.response.status === 401) {
          messageApi.error("Unauthorized. Please log in again.");
        } else {
          messageApi.error(`Failed to fetch leads (${err.response.status}).`);
        }
        console.error("API error:", err.response);
      } else if (err?.request) {
        messageApi.error(
          "Network/CORS error. Check SSL, CORS, or ad-blockers."
        );
        console.error("Request error (no response):", err.request);
      } else {
        messageApi.error("Unexpected error occurred.");
        console.error("Unexpected error:", err.message);
      }
    } finally {
      setLoading(false);
      setFetching(false);
    }
  };

  const handleConfirmApprove = async (lead) => {
    const id = lead?.LeeadID;
    if (!id) {
      messageApi.error("Lead ID missing.");
      return;
    }
    setApprovingId(id);
    try {
      const res = await api.post(
        "api/HighIntentLeads/ApproveLead",
        {},
        { params: { LeadID: id } }
      );
      const msg =
        (res?.data && (res.data.Message || res.data.message)) ||
        (typeof res?.data === "string"
          ? res.data
          : "Lead approved successfully.");
      messageApi.success(msg);
      setLeads((prev) => prev.filter((l) => l.LeeadID !== id));
    } catch (err) {
      const errMsg =
        err?.response?.data?.Message ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to approve lead.";
      messageApi.error(errMsg);
    } finally {
      setApprovingId(null);
    }
  };

  const inquiryOptions = useMemo(() => {
    const set = new Set(leads.map((l) => l.InquiryType).filter(Boolean));
    return Array.from(set).map((v) => ({ label: v, value: v }));
  }, [leads]);

  const stateOptions = useMemo(() => {
    const set = new Set(leads.map((l) => l.State).filter(Boolean));
    return Array.from(set).map((v) => ({ label: v, value: v }));
  }, [leads]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      const matchesQuery =
        !q ||
        [l.Name, l.ContactNo, l.City, l.State, l.Source, l.InquiryType]
          .filter(Boolean)
          .some((x) => String(x).toLowerCase().includes(q));
      const matchesInquiry = !inquiryFilter || l.InquiryType === inquiryFilter;
      const matchesState = !stateFilter || l.State === stateFilter;
      return matchesQuery && matchesInquiry && matchesState;
    });
  }, [leads, search, inquiryFilter, stateFilter]);

  const visibleLeads = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount]
  );

  const resetFilters = () => {
    setSearch("");
    setInquiryFilter(undefined);
    setStateFilter(undefined);
    setVisibleCount(isMobile ? 10 : 20);
  };

  const loadMore = () => {
    setVisibleCount((c) => c + (isMobile ? 10 : 20));
  };

  const tagColorByInquiry = (type) => {
    switch ((type || "").toLowerCase()) {
      case "wholesale":
        return "geekblue";
      case "retail":
        return "green";
      case "custom":
      case "bespoke":
        return "purple";
      default:
        return "gold";
    }
  };

  const openDialer = (phone) => {
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f5f6fa",
        padding: isMobile ? "8px" : "16px",
      }}
    >
      {contextHolder}

      {/* Sticky toolbar */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          background: "#f5f6fa",
          paddingBottom: 8,
        }}
      >
        <Row gutter={[8, 8]} align="middle" justify="space-between">
          <Col flex="auto">
            <Title
              level={isMobile ? 4 : 3}
              style={{ margin: 0, color: "#1890ff", fontWeight: 700 }}
            >
              High Intent Leads
            </Title>
            <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
              {filtered.length} lead{filtered.length === 1 ? "" : "s"}
            </Text>
          </Col>
          <Col>
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={() => fetchLeads(false)}
              loading={fetching}
              size={isMobile ? "middle" : "large"}
            >
              Refresh
            </Button>
          </Col>
        </Row>

        <Card
          size="small"
          style={{
            marginTop: 8,
            borderRadius: 10,
            border: "1px solid #e6f7ff",
          }}
          bodyStyle={{ padding: isMobile ? 8 : 12 }}
        >
          <Space direction="vertical" style={{ width: "100%" }} size={8}>
            <Input
              allowClear
              placeholder="Search name, phone, city, state, source..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setVisibleCount(isMobile ? 10 : 20);
              }}
              size={isMobile ? "middle" : "large"}
            />
            <Row gutter={[8, 8]}>
              <Col xs={12}>
                <Select
                  allowClear
                  placeholder="Inquiry Type"
                  options={inquiryOptions}
                  value={inquiryFilter}
                  onChange={(v) => {
                    setInquiryFilter(v);
                    setVisibleCount(isMobile ? 10 : 20);
                  }}
                  style={{ width: "100%" }}
                  size={isMobile ? "middle" : "large"}
                  suffixIcon={<FilterOutlined />}
                />
              </Col>
              <Col xs={12}>
                <Select
                  allowClear
                  placeholder="State"
                  options={stateOptions}
                  value={stateFilter}
                  onChange={(v) => {
                    setStateFilter(v);
                    setVisibleCount(isMobile ? 10 : 20);
                  }}
                  style={{ width: "100%" }}
                  size={isMobile ? "middle" : "large"}
                  suffixIcon={<FilterOutlined />}
                />
              </Col>
            </Row>
            {(search || inquiryFilter || stateFilter) && (
              <Button
                onClick={resetFilters}
                size={isMobile ? "middle" : "large"}
              >
                Clear filters
              </Button>
            )}
          </Space>
        </Card>
      </div>
      {loading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Spin size="large" />
        </div>
      ) : filtered.length === 0 ? (
        <Card
          style={{
            marginTop: 12,
            borderRadius: 12,
            border: "1px solid #f0f0f0",
          }}
        >
          <Empty description="No leads found" />
        </Card>
      ) : (
        <List
          style={{ marginTop: 12 }}
          dataSource={visibleLeads}
          renderItem={(item) => {
            const name = item?.Name || "Unknown";
            const phone = item?.ContactNo || "";
            const city = item?.City || "-";
            const state = item?.State || "-";
            const source = item?.Source || "-";
            const inquiry = item?.InquiryType || "-";
            const dateStr = item?.InquireDate
              ? moment(item.InquireDate).format("DD MMM YYYY")
              : "-";

            return (
              <Card
                style={{
                  marginBottom: 12,
                  borderRadius: 12,
                  border: "1px solid #e6f7ff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  cursor: "default",
                }}
                bodyStyle={{ padding: isMobile ? 12 : 16 }}
              >
                <Row gutter={[8, 8]} align="middle">
                  <Col xs={24}>
                    <Space
                      direction="horizontal"
                      align="center"
                      style={{ justifyContent: "space-between", width: "100%" }}
                    >
                      <Space direction="vertical" size={2}>
                        <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
                          {name}
                        </Text>
                        <Text
                          type="secondary"
                          style={{ fontSize: isMobile ? 12 : 13 }}
                        >
                          {dateStr} • {source}
                        </Text>
                      </Space>
                      <Tag
                        color={tagColorByInquiry(inquiry)}
                        style={{ fontSize: 12 }}
                      >
                        {inquiry}
                      </Tag>
                    </Space>
                  </Col>

                  <Col xs={24}>
                    <Row gutter={[8, 8]}>
                      <Col xs={12}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          City
                        </Text>
                        <div style={{ fontSize: 13 }}>{city}</div>
                      </Col>
                      <Col xs={12}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          State
                        </Text>
                        <div style={{ fontSize: 13 }}>{state}</div>
                      </Col>
                      <Col xs={24}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Contact
                        </Text>
                        <div style={{ fontSize: 13 }}>{phone || "-"}</div>
                      </Col>
                    </Row>
                  </Col>

                  <Col xs={24}>
                    <Space
                      style={{ width: "100%", justifyContent: "flex-end" }}
                    >
                      <Button
                        icon={<PhoneOutlined />}
                        size={isMobile ? "middle" : "large"}
                        onClick={(e) => {
                          e.stopPropagation();
                          openDialer(item?.ContactNo);
                        }}
                      >
                        Call
                      </Button>
                      <Popconfirm
                        title="Approve this lead?"
                        description={
                          <div style={{ lineHeight: 1.6 }}>
                            <div>
                              <Text strong>Name:</Text>{" "}
                              <Text>{item?.Name || "-"}</Text>
                            </div>
                            <div>
                              <Text strong>Contact:</Text>{" "}
                              <Text>{item?.ContactNo || "-"}</Text>
                            </div>
                            <div>
                              <Text strong>Location:</Text>{" "}
                              <Text>
                                {item?.City || "-"}, {item?.State || "-"}
                              </Text>
                            </div>
                            <div>
                              <Text strong>Inquiry:</Text>{" "}
                              <Text>{item?.InquiryType || "-"}</Text>
                            </div>
                            <div>
                              <Text strong>Source:</Text>{" "}
                              <Text>{item?.Source || "-"}</Text>
                            </div>
                            <div>
                              <Text strong>Date:</Text>{" "}
                              <Text>
                                {item?.InquireDate
                                  ? moment(item.InquireDate).format(
                                      "DD MMM YYYY"
                                    )
                                  : "-"}
                              </Text>
                            </div>
                          </div>
                        }
                        onConfirm={() => handleConfirmApprove(item)}
                        okText="Approve"
                        cancelText="Cancel"
                        okButtonProps={{
                          type: "primary",
                          icon: <CheckCircleOutlined />,
                          loading: approvingId === item?.LeeadID,
                        }}
                        placement={isMobile ? "topRight" : "top"}
                      >
                        <Button
                          icon={<CheckCircleOutlined />}
                          size={isMobile ? "middle" : "large"}
                          type="primary"
                          loading={approvingId === item?.LeeadID}
                        >
                          Approve
                        </Button>
                      </Popconfirm>
                    </Space>
                  </Col>
                </Row>
              </Card>
            );
          }}
        />
      )}
      {!loading && filtered.length > visibleLeads.length && (
        <div style={{ textAlign: "center", margin: "12px 0 24px" }}>
          <Button onClick={loadMore} size={isMobile ? "middle" : "large"}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
};

export default LeadsToApprove;
